import React from 'react';
import WizardSelectControlledComponent from 'components/v2/epd-wizard-inputs/WizardSelectControlled';
import { confirmAlert } from 'components/v2/confirm-dialog/confirmAlert';
import { useDocumentStore } from '../hooks/useDocumentStore';
import usePropertyPath from '../hooks/usePropertyPath';
import useProperty from '../hooks/useProperty';
import useDataSource from '../hooks/useDataSource';
import { SelectInputRecord } from 'types/dynamic-forms/records/SelectInputRecord';
import { getDocumentSchemaId } from 'services/api/dynamicFormsApi';

const PcrSelectorRenderer: React.FC<SelectInputRecord> = ({ propertyId, label, tooltip }) => {
  const { setSchemaId, getSchemaId } = useDocumentStore();
  const propertyPath = usePropertyPath(propertyId);
  const { value, setValue, isVisible, errorMessage, isDisabled } = useProperty<string | null>(propertyPath);

  const { options } = useDataSource(propertyPath);
  if (!isVisible) {
    return null;
  }

  const applyPcrSelectionAsync = async (nextPcrId: string, nextSchemaId: string, shouldRebuild: boolean) => {
    setValue(nextPcrId);
    if (shouldRebuild) {
      await setSchemaId(nextSchemaId);
    }
  };

  const handleChange = async (nextValue: string | null | undefined) => {
    if (!nextValue || nextValue === value) {
      return;
    }
    const selectedOption = options.find((o) => o.value === nextValue);
    if (selectedOption?.isDisabled) {
      return;
    }

    const currentSchemaId = getSchemaId();
    const nextSchemaId = await getDocumentSchemaId(nextValue);
    const shouldRebuild = currentSchemaId !== nextSchemaId;

    if (!shouldRebuild) {
      await applyPcrSelectionAsync(nextValue, nextSchemaId, false);
      return;
    }

    const selectedPcrLabel = selectedOption?.label ?? nextValue;

    confirmAlert({
      caption: 'Confirm PCR change',
      description: (
        <>
          <p>The selected PRC "{selectedPcrLabel}" is configured to have custom data.</p>
          <p>The fields on the form will be changed which can lead to losing allready added data.</p>
          <p>Please press "Confirm" to procced or press "Cancel" to rollback the PCR selection.</p>
        </>
      ),
      acceptLabel: 'Confirm',
      rejectLabel: 'Cancel',
      onConfirm: () => applyPcrSelectionAsync(nextValue, nextSchemaId, true),
    });
  };

  return (
    <WizardSelectControlledComponent
      name="pcrSelector"
      options={options}
      value={options.find((o) => o.value === value) ?? null}
      label={label}
      tooltip={tooltip}
      onChanged={(_: string, newValue: any) => handleChange(newValue)}
      error={errorMessage}
      disabled={isDisabled}
    />
  );
};

export default PcrSelectorRenderer;
